import {
  apiCall,
  generateHeaders,
  generateAuthHeaders,
} from "@/lib/api/apiUtil";
import { commCenterServiceLogger as LOGGER } from "@/lib/logger/logger";

const PREFIX = process.env.API_URL;
const COMM_CENTER_MESSAGES = `${PREFIX}/messages`;
const COMM_CENTER_TICKETS_FOR_USER = `${PREFIX}/tickets/for-external-user`;
const COMM_CENTER_TICKETS = `${PREFIX}/tickets`;
const COMM_CENTER_COUNTS = `${PREFIX}/tickets/unread-counts`;
const COMM_CENTER_MESSAGE_ATTACHMENT = `${PREFIX}/messages/download-attachment`;
const COMM_CENTER_TICKET_STATUS = `${PREFIX}/tickets/change-status`;
const COMM_CENTER_MESSAGES_SENT_BY_USER = `${PREFIX}/messages/sent-by-pixe-user`;
const COMM_CENTER_NOTIFICATIONS_BY_USER = `${PREFIX}/odm/notifications/by-user`;
const COMM_CENTER_NOTIFICATION_DOWNLOAD = `${PREFIX}/odm/notifications/download`;

interface Params {
  [key: string]: any;
}

export const getCounts = async () => {
  const response = await fetch('/api/comm-center/counts');
  return response.json();
};

export default class CommCenterService {
  static async sendMessagePipe(
    username: string,
    userId: string,
    mpin: string,
    formData: FormData
  ) {
    const qs = {
      fromExternalId: userId,
      fromExternalUsername: username,
      physicianMpin: mpin,
      origin: "EXTERNAL",
    };
    const response = await apiCall(
      {
        method: "POST",
        url: COMM_CENTER_MESSAGES,
        headers: generateHeaders(),
        params: qs,
        data: formData
      },
      LOGGER,
      COMM_CENTER_MESSAGES,
    );
    return {
      data: response.data,
      status: response.status,
      statusText: response.statusText
    };
  }

  static async getTicketsForUser(
    userId: string,
    query: Params,
  ) {
    const response = await apiCall(
      {
        method: "GET",
        url: `${COMM_CENTER_TICKETS_FOR_USER}/${userId}`,
        headers: generateHeaders(),
        params: query,
      },
      LOGGER,
      COMM_CENTER_TICKETS_FOR_USER,
    );
    return {
      data: response.data,
      status: response.status,
      statusText: response.statusText,
      totalItems: Number(response.headers["x-total-count"]),
    };
  }

  static async getTicket(
    ticketId: string,
    lastMessageIdToShow: string,
    userId: string,
  ) {
    const response = await apiCall(
      {
        method: "GET",
        url: `${COMM_CENTER_TICKETS}/${ticketId}`,
        headers: generateHeaders(),
        params: {
          userId,
          lastMessageIdToShow,
        },
      },
      LOGGER,
      COMM_CENTER_TICKETS,
    );
    return {
      data: response.data,
      status: response.status,
      statusText: response.statusText
    };
  }

  static async getCounts(userId: string) {
    const response = await apiCall(
      {
        method: "GET",
        url: `${COMM_CENTER_COUNTS}/${userId}`,
        headers: generateHeaders(),
        params: { size: 1000 }, // TODO remove after API change
      },
      LOGGER,
      COMM_CENTER_COUNTS,
    );
    return {
      data: response.data,
      status: response.status,
      statusText: response.statusText
    };
  }

  static async getMessageAttachment(
    id: string,
    query: Params,
  ) {
    const response = await apiCall(
      {
        method: "GET",
        url: `${COMM_CENTER_MESSAGE_ATTACHMENT}/${id}`,
        headers: generateHeaders(),
        params: query,
        responseType: 'arraybuffer'
      },
      LOGGER,
      COMM_CENTER_MESSAGE_ATTACHMENT,
    );
    return {
      data: response.data,
      status: response.status,
      statusText: response.statusText
    };
  }

  static async changeTicketStatus(
    ticketIds: string[],
    archive: boolean,
  ) {
    const ticketStatus = archive ? "CLOSED" : "OPEN";
    const response = await apiCall(
      {
        method: "PUT",
        url: COMM_CENTER_TICKET_STATUS,
        headers: generateHeaders(),
        params: {
          ticketIds,
          ticketStatus,
        },
      },
      LOGGER,
      COMM_CENTER_TICKET_STATUS,
    );
    return {
      data: response.data,
      status: response.status,
      statusText: response.statusText
    };
  }

  static async getMessagesSentByUser(
    userId: string,
    query: Params,
  ) {
    const response = await apiCall(
      {
        method: "GET",
        url: `${COMM_CENTER_MESSAGES_SENT_BY_USER}/${userId}`,
        headers: generateHeaders(),
        params: query,
      },
      LOGGER,
      COMM_CENTER_MESSAGES_SENT_BY_USER,
    );
    return {
      data: response.data,
      status: response.status,
      statusText: response.statusText,
      totalItems: Number(response.headers["x-total-count"]),
    };
  }

  static async getNotificationsForUser(
    userId: string,
    username: string,
    query: Params,
  ) {
    const response = await apiCall(
      {
        method: "GET",
        url: `${COMM_CENTER_NOTIFICATIONS_BY_USER}/${userId}`,
        headers: generateAuthHeaders(username),
        params: query,
      },
      LOGGER,
      COMM_CENTER_NOTIFICATIONS_BY_USER,
    );
    return {
      data: response.data,
      status: response.status,
      statusText: response.statusText,
      totalItems: Number(response.headers["x-total-count"]),
    };
  }

  static async downloadNotification(
    notificationId: string,
    username: string,
  ) {
    const response = await apiCall(
      {
        method: "GET",
        url: `${COMM_CENTER_NOTIFICATION_DOWNLOAD}/${notificationId}`,
        headers: generateAuthHeaders(username),
        responseType: "arraybuffer",
      },
      LOGGER,
      COMM_CENTER_NOTIFICATION_DOWNLOAD,
    );
    return {
      data: response.data,
      status: response.status,
      statusText: response.statusText
    };
  }

  static async deleteMessagePipe(messageId: string) {
    const response = await apiCall(
      {
        method: "DELETE",
        url: `${COMM_CENTER_MESSAGES}/${messageId}`,
        headers: generateHeaders(),
      },
      LOGGER,
      `${COMM_CENTER_MESSAGES}/${messageId}`,
    );
    return {
      data: response.data,
      status: response.status,
      statusText: response.statusText
    };
  }
}
